import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { testRunService } from '@/database/services'

export const useTestRunStore = defineStore('testRun', () => {
  // State
  const testRuns = ref([])
  const loading = ref(false)
  const error = ref(null)

  // Getters
  const getTestRuns = computed(() => testRuns.value)
  const getLoading = computed(() => loading.value)
  const getError = computed(() => error.value)

  // Actions
  const fetchTestRuns = async (filters = {}) => {
    try {
      loading.value = true
      error.value = null
      const data = await testRunService.getAll(filters)
      testRuns.value = data
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching test runs:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchTestRunById = async (id) => {
    try {
      loading.value = true
      error.value = null
      const data = await testRunService.getById(id)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching test run:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const createTestRun = async (testRunData) => {
    try {
      loading.value = true
      error.value = null
      const data = await testRunService.create(testRunData)
      testRuns.value.push(data)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error creating test run:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateTestRun = async (id, testRunData) => {
    try {
      loading.value = true
      error.value = null
      const data = await testRunService.update(id, testRunData)
      const index = testRuns.value.findIndex(tr => tr.id === id)
      if (index !== -1) {
        testRuns.value[index] = data
      }
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error updating test run:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const deleteTestRun = async (id) => {
    try {
      loading.value = true
      error.value = null
      await testRunService.delete(id)
      testRuns.value = testRuns.value.filter(tr => tr.id !== id)
      return true
    } catch (err) {
      error.value = err.message
      console.error('Error deleting test run:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchTestRunsByProject = async (projectId) => {
    try {
      loading.value = true
      error.value = null
      const data = await testRunService.getByProject(projectId)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching test runs by project:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchTestRunsByStatus = async (projectId, status) => {
    try {
      loading.value = true
      error.value = null
      const data = await testRunService.getByStatus(projectId, status)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching test runs by status:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const startTestRun = async (id) => {
    try {
      loading.value = true
      error.value = null
      const data = await testRunService.start(id)
      const index = testRuns.value.findIndex(tr => tr.id === id)
      if (index !== -1) {
        testRuns.value[index] = data
      }
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error starting test run:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const completeTestRun = async (id) => {
    try {
      loading.value = true
      error.value = null
      const data = await testRunService.complete(id)
      const index = testRuns.value.findIndex(tr => tr.id === id)
      if (index !== -1) {
        testRuns.value[index] = data
      }
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error completing test run:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const clearError = () => {
    error.value = null
  }

  const clearTestRuns = () => {
    testRuns.value = []
  }

  // Convenience methods that match what views expect
  const getTestRunsAsync = async (filters = {}) => {
    try {
      loading.value = true
      error.value = null
      const data = await testRunService.getTestRunsWithFilters(filters)
      testRuns.value = data.data || []
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching test runs with filters:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const getTestRun = async (id) => {
    return await fetchTestRunById(id)
  }

  return {
    // State
    testRuns,
    loading,
    error,
    // Getters
    getTestRuns, // computed getter
    getLoading,
    getError,
    // Actions
    fetchTestRuns,
    fetchTestRunById,
    createTestRun,
    updateTestRun,
    deleteTestRun,
    fetchTestRunsByProject,
    fetchTestRunsByStatus,
    startTestRun,
    completeTestRun,
    clearError,
    clearTestRuns,
    // Convenience methods - aliased to avoid duplicate key warnings
    fetchTestRunsAsync: getTestRunsAsync, // async method for API calls
    getTestRunById: getTestRun
  }
})
